import {
  PaidPricingTableKey,
  paidPricingTableKeySchema,
} from 'base/accounts_service';
import { UnreachableError } from 'base/helpers';
import { currentUrl } from 'blocks/current_url';
import {
  AcceptPlanDialogUrlState,
  parseAcceptPlanDialogUrlState,
  serializeAcceptPlanDialogUrlState,
} from './accept_plan_offer';

export type AuthRouteNoContext =
  | {
      path: 'login';
    }
  | {
      path: 'signup';
    }
  | {
      path: 'signup/verify-email';
      userId: number;
      email: string;
    }
  | {
      path: 'signup/choose-plan';
      dialog?: AcceptPlanDialogUrlState;
    }
  | {
      path: 'signup/invite-team';
    }
  | {
      path: 'signup/survey';
    }
  | {
      path: 'forgot-password';
    }
  | {
      path: 'forgot-password/verify-email';
      email: string;
    }
  | {
      path: 'forgot-password/change-password';
      entitlement: string;
    }
  | {
      path: 'upgrade';
      dialog?: AcceptPlanDialogUrlState;
    };

export type AuthRoute = AuthRouteNoContext & {
  context: {
    redirect?: string;
    selectPlan?: PaidPricingTableKey;
    skipPricingTable?: boolean;
    oauthKey?: string;
  };
};

function parseContext(params: URLSearchParams): AuthRoute['context'] {
  const redirect = params.get('redirect') || undefined;
  if (redirect != null) {
    if (redirect.startsWith('//') || !redirect.startsWith('/')) {
      throw new Error('Invalid insecure redirect');
    }
  }
  const selectPlanRaw = params.get('selectPlan') || undefined;
  const selectPlan = selectPlanRaw
    ? paidPricingTableKeySchema.safeParse(selectPlanRaw)
    : undefined;
  const skipPricingTable = optionalBoolParam(params, 'skipPricingTable');
  const oauthKey = params.get('oauthKey') || undefined;
  return {
    redirect,
    selectPlan: selectPlan?.success ? selectPlan.data : undefined,
    skipPricingTable,
    oauthKey,
  };
}

function requiredStringParam(params: URLSearchParams, name: string) {
  const value = params.get(name);
  if (!value) throw new Error(`Missing ${name}`);
  return value;
}

function requiredHashParam(params: URLSearchParams, name: string) {
  const value = params.get(name);
  if (!value) throw new Error(`Missing ${name}`);
  return value;
}

function optionalIntParam(params: URLSearchParams, name: string) {
  const value = params.get(name);
  if (!value) return undefined;
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) throw new Error(`Invalid ${name}`);
  return parsed;
}
function requiredIntParam(params: URLSearchParams, name: string) {
  const val = optionalIntParam(params, name);
  if (val == null) throw new Error(`Missing ${name}`);
  return val;
}

function optionalBoolParam(params: URLSearchParams, name: string) {
  const value = params.get(name);
  if (!value) return undefined;
  return value === 'true';
}

export function maybeParseAuthRoute(
  url: Pick<URL, 'searchParams' | 'pathname'>,
): AuthRoute | undefined {
  const p = url.searchParams;
  if (url.pathname === '/app/login') {
    return { path: 'login', context: parseContext(p) };
  }
  if (url.pathname === '/app/signup') {
    return { path: 'signup', context: parseContext(p) };
  }
  if (url.pathname === '/app/signup/verify-email') {
    return {
      path: 'signup/verify-email',
      context: parseContext(p),
      userId: requiredIntParam(p, 'userId'),
      email: requiredStringParam(p, 'email'),
    };
  }
  if (url.pathname === '/app/signup/choose-plan') {
    return {
      path: 'signup/choose-plan',
      context: parseContext(p),
      dialog: parseAcceptPlanDialogUrlState(p),
    };
  }
  if (url.pathname === '/app/signup/invite-team') {
    return { path: 'signup/invite-team', context: parseContext(p) };
  }
  if (url.pathname === '/app/signup/survey') {
    return { path: 'signup/survey', context: parseContext(p) };
  }
  if (url.pathname === '/app/forgot-password') {
    return { path: 'forgot-password', context: parseContext(p) };
  }
  if (url.pathname === '/app/forgot-password/verify-email') {
    return {
      path: 'forgot-password/verify-email',
      context: parseContext(p),
      email: requiredStringParam(p, 'email'),
    };
  }
  if (url.pathname === '/app/forgot-password/change-password') {
    return {
      path: 'forgot-password/change-password',
      context: parseContext(p),
      entitlement: requiredStringParam(p, 'entitlement'),
    };
  }
  if (url.pathname === '/app/upgrade') {
    return {
      path: 'upgrade',
      context: parseContext(p),
      dialog: parseAcceptPlanDialogUrlState(p),
    };
  }
}

export function getCurrentLocationForRedirect() {
  const { pathname, search } = currentUrl;
  const { hash } = window.location;
  return `${pathname}${search}${hash}`;
}

export function authRouteToUrl(route: AuthRoute) {
  const search = new URLSearchParams();
  if (route.context.redirect) {
    search.set('redirect', route.context.redirect);
  }
  if (route.context.selectPlan) {
    search.set('selectPlan', route.context.selectPlan);
  }
  if (route.context.skipPricingTable) {
    search.set('skipPricingTable', 'true');
  }
  if (route.context.oauthKey) {
    search.set('oauthKey', route.context.oauthKey);
  }

  switch (route.path) {
    case 'login':
      return `/app/login?${search}`;
    case 'signup':
      return `/app/signup?${search}`;
    case 'signup/verify-email':
      search.set('userId', route.userId.toString());
      search.set('email', route.email);
      return `/app/signup/verify-email?${search}`;
    case 'signup/choose-plan':
      serializeAcceptPlanDialogUrlState(route.dialog, search);
      return `/app/signup/choose-plan?${search}`;
    case 'signup/invite-team':
      return `/app/signup/invite-team?${search}`;
    case 'signup/survey':
      return `/app/signup/survey?${search}`;
    case 'forgot-password':
      return `/app/forgot-password?${search}`;
    case 'forgot-password/verify-email':
      search.set('email', route.email);
      return `/app/forgot-password/verify-email?${search}`;
    case 'forgot-password/change-password':
      search.set('entitlement', route.entitlement);
      return `/app/forgot-password/change-password?${search}`;
    case 'upgrade':
      serializeAcceptPlanDialogUrlState(route.dialog, search);
      return `/app/upgrade?${search}`;
    default:
      throw new UnreachableError(route);
  }
}

export const TOAST_ID_SEARCH_PARAM = 'toast-id';
export const INVALID_AUTH_TOAST_ID = 'invalid-auth';

export function consumeSearchParam(key: string) {
  const url = new URL(window.location.href);
  const searchParam = url.searchParams.get(key);
  if (searchParam === null) {
    return undefined; // Search param not found
  }

  // Remove key from search params
  url.searchParams.delete(key);
  window.history.replaceState({}, '', url.toString());

  return searchParam;
}
